import { AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";
import { useCreateCustomerPortal, useInvoicePaymentLink } from "@/queries/organizations";
import {
  getPaymentIssueMessage,
  getPaymentIssueAction,
  getPaymentIssueButtonLabel,
} from "@/utils/subscriptionStatus";
import LoadingSpinner from "@/components/LoadingSpinner";

export const PaymentIssueBanner = () => {
  const { stripe_subscription_status, hasPaymentIssue, isLoadingSubscriptionStatus } =
    useOrganizationSubscriptionData();

  const { mutate: createCustomerPortal, isLoading: isLoadingPortal } = useCreateCustomerPortal();
  const { mutate: getInvoicePaymentLink, isLoading: isLoadingInvoice } = useInvoicePaymentLink();

  const isLoading = isLoadingPortal || isLoadingInvoice;

  if (!hasPaymentIssue || !stripe_subscription_status || isLoadingSubscriptionStatus) {
    return null;
  }

  const message = getPaymentIssueMessage(stripe_subscription_status);
  const action = getPaymentIssueAction(stripe_subscription_status);
  const buttonLabel = getPaymentIssueButtonLabel(stripe_subscription_status);

  const handleAction = () => {
    if (action === "pay_invoice") {
      getInvoicePaymentLink();
    } else if (action === "update_payment_method") {
      createCustomerPortal({ return_url: window.location.href });
    }
  };

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{
        height: "auto",
        opacity: 1,
      }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="flex items-center justify-between overflow-hidden border-b border-amber-200 bg-amber-50 px-6"
    >
      <div className="flex flex-1 items-center justify-between py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-xs text-amber-700">{message}</p>
            </div>
          </div>
        </div>
        <div className="ml-4 flex flex-shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleAction}
            disabled={isLoading}
            className="h-7 border-amber-600 bg-transparent text-amber-600 hover:border-amber-700 hover:bg-amber-100 hover:text-amber-700"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size={4} />
                <span className="ml-2">Loading...</span>
              </>
            ) : (
              buttonLabel
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
